import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { FAR_STATE, FAR_LINK_RECIPIENT } from "../../../../assets/js/utils/far/farEngine";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";
import { FAR_STATE_TONE, FarNotice } from "./farUi";
import FarRuleDetails from "./FarRuleDetails";

const useStyles = makeStyles(theme => {
    const s = farShared(theme);
    const t = s.t;
    return {
        wrap: { marginTop: "6px" },
        notice: { padding: "8px 10px !important", fontSize: "13px !important", lineHeight: "18px !important" },
        toggle: {
            ...s.focusRing,
            display: "inline-flex", alignItems: "center", gap: "2px", background: "none", border: "none", padding: 0,
            color: t.link, fontWeight: 600, fontSize: "13px", cursor: "pointer", whiteSpace: "nowrap",
            "& svg": { width: "18px", height: "18px", transition: "transform 0.2s" }
        },
        open: { "& svg": { transform: "rotate(180deg)" } },
        details: { marginTop: "6px", display: "flex", flexDirection: "column", gap: "8px" },
        fileLabel: { fontSize: "12px", fontWeight: 700, color: t.textMuted, margin: "0 0 2px", wordBreak: "break-all" }
    };
});

const list = (emails) => (emails && emails.length > 0 ? emails.join(", ") : "no one (no common authorized recipient)");

export function getFileStatusMessage(item, settings){
    const showAllowed = settings.show_authorized_recipients !== false;
    const files = item.files || [];
    const first = files[0] || null;
    const allowed = first ? first.allowedRecipients : [];
    const ruleCount = first ? first.matches.length : 0;
    const folderPrefix = item.isFolder ? files.length+" of "+item.totalFiles+" files match a rule. " : "";

    switch(item.state){
        case FAR_STATE.NONE:
            return item.isFolder ? "No files in this folder match a File Attachment Rule." : "File does not match any File Attachment Rule.";
        case FAR_STATE.MATCHED:
            return folderPrefix + (showAllowed ? "This file can only be sent to: "+list(allowed) : "Matches "+ruleCount+" File Attachment Rule"+(ruleCount === 1 ? "" : "s")+". Add recipients to check them.");
        case FAR_STATE.AUTHORIZED:
            return folderPrefix + "File matches "+ruleCount+" File Attachment Rule"+(ruleCount === 1 ? "" : "s")+". All recipients are authorized.";
        case FAR_STATE.OVERRIDDEN: {
            const recipients = {};
            files.forEach(f => (f.pairs || []).forEach(p => { if(!p.authorized && p.exception){ recipients[p.recipient] = true; } }));
            const names = Object.keys(recipients).map(r => (r === FAR_LINK_RECIPIENT ? "share link" : r));
            return folderPrefix + "Exception authorized for: "+names.join(", ")+".";
        }
        case FAR_STATE.LINK:
            return folderPrefix + "Share links can't confirm who opens them."+(showAllowed ? " This file can only be sent by email to: "+list(allowed) : "");
        case FAR_STATE.MIXED: {
            const bad = first ? first.unauthorizedRecipients.length : 0;
            const total = first ? first.unauthorizedRecipients.length + first.authorizedRecipients.length : 0;
            return folderPrefix + bad+" of "+total+" recipients are not authorized."+(showAllowed ? " This file can only be sent to: "+list(allowed) : "");
        }
        case FAR_STATE.EXCLUDED: {
            const blocked = {};
            files.forEach(f => (f.pairs || []).forEach(p => {
                if(p.excluded && !p.exception && !blocked[p.recipient]){
                    blocked[p.recipient] = p.exclusionType === "domain" ? p.recipient+" (domain "+p.exclusionValue+")" : p.recipient;
                }
            }));
            return folderPrefix + "Blocked by FAR exclusion: "+Object.keys(blocked).map(r => blocked[r]).join(", ")+" cannot receive this file.";
        }
        case FAR_STATE.UNAUTHORIZED:
        default:
            return folderPrefix + (showAllowed ? "This file can only be sent to: "+list(allowed) : "A recipient is not authorized for this file.");
    }
}

function FarFileStatus({ item, settings, id }){
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    if(!item){
        return null;
    }
    const tone = FAR_STATE_TONE[item.state] || "neutral";
    const canExpand = item.state !== FAR_STATE.NONE && settings.show_rule_details !== false;
    const detailsId = id+"-details";

    return (
        <div className={classes.wrap}>
            <FarNotice tone={tone} className={classes.notice} role="status" id={id}
                action={
                    canExpand ?
                        <button type="button" className={classes.toggle+" "+(open ? classes.open : "")} aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(!open)}>
                            {open ? "Hide Details" : "View Details"}<ExpandMore aria-hidden="true" />
                        </button>
                    : null
                }
            >
                {getFileStatusMessage(item, settings)}
            </FarNotice>
            {
                canExpand && open ?
                    <div className={classes.details} id={detailsId}>
                        {
                            item.files.slice(0, 20).map(file => (
                                <div key={file.path}>
                                    { item.isFolder ? <p className={classes.fileLabel}>{file.path}</p> : null }
                                    { file.matches.map(match => <FarRuleDetails key={match.rule.id} rule={match.rule} evaluation={match.evaluation} settings={settings} file={file} compact={true} />) }
                                </div>
                            ))
                        }
                        { item.files.length > 20 ? <p className={classes.fileLabel}>…and {item.files.length - 20} more matching files.</p> : null }
                    </div>
                : null
            }
        </div>
    );
}

FarFileStatus.propTypes = {
    item: PropTypes.object,
    settings: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired
};

export default FarFileStatus;
